/**
 * DV-STAGE-003: Stage Card Component (Updated)
 * Individual stage testing card with DV-STAGE-003 input sources system
 */

import { useState, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { cn } from '@/utils/cn';
import { type LogLevel } from './LogsPanel';
import { InputSourcePicker } from './InputSourcePicker';
import { PromptPreview, type PromptPreviewData } from './PromptPreview';
import { RunControls, type RunResult, type StageOverrides as RunControlsOverrides } from './RunControls';
import { useStageTester } from '@/hooks/useStageTester';
import { StageId, StageOverrides } from '@/types/stage-tester';

// Re-export for backward compatibility
export type { StageId } from '@/types/stage-tester';

export interface StageCardState {
  promptPreview: PromptPreviewData | null;
  promptHash: string | null;
  validationRequestId: string | null;
  sandboxMode: boolean;
  saveToExecution: boolean;
  isRunning: boolean;
  lastRun: RunResult | null;
  expanded: boolean;
}

// Re-export for backward compatibility
export type StageTesterState = StageCardState;

export interface StageCardProps {
  stageId: StageId;
  title: string;
  description: string;
  state: StageCardState;
  onChange: (updates: Partial<StageCardState>) => void;
  onRun: () => void;
  onCancel: () => void;
  onLog: (entry: { ts: string; level: LogLevel; text: string; meta?: any }) => void;
  className?: string;
  'data-testid'?: string;
}

export function StageCard({
  stageId,
  title,
  description,
  state,
  onChange,
  onRun,
  onCancel,
  onLog,
  className,
  'data-testid': testId = `stage-card-${stageId}`
}: StageCardProps) {
  const [showLocalLogs, setShowLocalLogs] = useState(false);

  // Use the new DV-STAGE-003 stage tester hook
  const stageTester = useStageTester({
    stage: stageId,
    onLog
  });

  // Stabilize onLog callback
  const logRef = useRef(onLog);
  logRef.current = onLog;

  // Handle overrides changes
  const handleOverridesChange = useCallback((overrides: RunControlsOverrides) => {
    // Convert RunControls format to stage tester format
    const stageOverrides: StageOverrides = {
      provider: overrides.provider,
      model: overrides.model,
      params: {
        temperature: overrides.temperature,
        topP: overrides.topP,
        seed: overrides.seed,
        tools: overrides.tools,
        maxCompletionTokens: overrides.maxCompletionTokens
      }
    };
    stageTester.setOverrides(stageOverrides);
  }, [stageTester]);

  // Convert stage overrides back to RunControls format
  const runControlsOverrides: RunControlsOverrides = {
    provider: (stageTester.state.overrides?.provider as 'openai' | 'anthropic' | 'google') || 'openai',
    model: stageTester.state.overrides?.model || 'gpt-5',
    temperature: stageTester.state.overrides?.params?.temperature || 1.0, // GPT-5 default
    topP: stageTester.state.overrides?.params?.topP || 1.0,
    seed: stageTester.state.overrides?.params?.seed || null,
    tools: stageTester.state.overrides?.params?.tools || false,
    maxCompletionTokens: stageTester.state.overrides?.params?.maxCompletionTokens || 4096
  };

  // Handle sandbox/save toggles
  const handleModeChange = useCallback((sandboxMode: boolean, saveToExecution: boolean) => {
    onChange({ sandboxMode, saveToExecution });

    onLog({
      ts: new Date().toISOString(),
      level: 'debug',
      text: `Mode changed for ${stageId}: sandbox=${sandboxMode}, save=${saveToExecution}`,
      meta: {
        stage: stageId,
        sandboxMode,
        saveToExecution,
        source: 'stage_card',
      }
    });
  }, [stageId, onChange, onLog]);

  // Copy stage status for individual stage debugging
  const copyStageStatus = useCallback(async () => {
    const status = {
      stage: stageId,
      input_source: stageTester.state.source,
      sample_id: stageTester.state.sampleId || 'none',
      custom_input_hash: stageTester.state.customJson ? `${stageTester.state.customJson.length}b` : 'none',
      effective_input_hash: stageTester.inputHash,
      prompt_hash: state.promptHash || 'none',
      validation_request_id: state.validationRequestId?.slice(-8) || 'none',
      overrides: stageTester.state.overrides,
      sandbox_mode: state.sandboxMode,
      save_to_execution: state.saveToExecution,
      is_running: state.isRunning,
      last_run: state.lastRun ? {
        timestamp: state.lastRun.timestamp,
        success: state.lastRun.success,
        duration_ms: state.lastRun.duration,
        cost_usd: state.lastRun.cost,
        tokens: state.lastRun.tokens,
        request_id: state.lastRun.requestId?.slice(-8) || 'none',
      } : null,
      input_error: stageTester.inputError,
      samples_count: stageTester.samples.length,
      samples_error: stageTester.samplesError,
      storage_info: stageTester.getStorageInfo(),
      generated_at: new Date().toISOString(),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(status, null, 2));
      onLog({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Stage status copied for ${stageId}`,
        meta: {
          stage: stageId,
          source: 'stage_card',
        }
      });
    } catch (error) {
      onLog({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to copy stage status for ${stageId}`,
        meta: {
          stage: stageId,
          error: error instanceof Error ? error.message : String(error),
          source: 'stage_card',
        }
      });
    }
  }, [stageId, stageTester, state, onLog]);

  return (
    <div
      className={cn(
        "border rounded-lg overflow-hidden transition-all duration-200",
        state.expanded && "ring-2",
        className
      )}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: state.expanded ? 'var(--border-interactive)' : 'var(--border-default)',
        ['--ring-color' as any]: 'var(--border-focus)',
      }}
      data-testid={testId}
    >
      {/* Header */}
      <button
        onClick={() => onChange({ expanded: !state.expanded })}
        className="w-full flex items-center justify-between p-4 text-left transition-colors focus:outline-none focus:ring-2"
        style={{
          backgroundColor: state.expanded ? 'var(--bg-elevated)' : 'var(--bg-surface)',
        }}
        data-testid={`${testId}-header`}
      >
        <div className="flex items-center gap-3">
          {state.expanded ? (
            <ChevronDown className="w-5 h-5" style={{ color: 'var(--fg-muted)' }} />
          ) : (
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--fg-muted)' }} />
          )}

          <div>
            <h3
              className="font-mono font-medium"
              style={{
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              {title}
            </h3>
            <p
              className="text-sm mt-1"
              style={{ color: 'var(--fg-muted)' }}
            >
              {description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Status indicators */}
          {state.isRunning && (
            <div
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-mono"
              style={{
                backgroundColor: 'var(--warning)',
                color: 'var(--bg-app)',
              }}
            >
              <div className="w-2 h-2 rounded-full bg-current animate-pulse" />
              Running
            </div>
          )}

          {state.lastRun && !state.isRunning && (
            <div
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-mono"
              style={{
                backgroundColor: state.lastRun.success ? 'var(--success)' : 'var(--danger)',
                color: 'var(--bg-app)',
              }}
            >
              {state.lastRun.success ? '✓' : '✗'}
              {state.lastRun.duration}ms
            </div>
          )}

          {!state.sandboxMode && (
            <div
              className="px-2 py-1 rounded text-xs font-mono"
              style={{
                backgroundColor: 'var(--danger)',
                color: 'var(--bg-app)',
              }}
            >
              LIVE
            </div>
          )}

          {/* Samples count indicator */}
          <div
            className="px-2 py-1 rounded text-xs font-mono"
            style={{
              backgroundColor: stageTester.samples.length > 0 ? 'var(--success)' : 'var(--fg-muted)',
              color: 'var(--bg-app)',
            }}
          >
            {stageTester.samples.length} samples
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {state.expanded && (
        <div
          className="border-t"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <div className="p-4 space-y-6">
            {/* Inputs Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4
                  className="text-sm font-mono font-medium"
                  style={{
                    color: 'var(--accent)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  Inputs
                </h4>
                <button
                  onClick={stageTester.clearState}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                  title="Clear stage state"
                >
                  Clear
                </button>
              </div>

              {/* Input Error Display */}
              {stageTester.inputError && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  <strong>Input Error:</strong> {stageTester.inputError}
                </div>
              )}

              <InputSourcePicker
                stage={stageId}
                source={stageTester.state.source}
                onSourceChange={stageTester.setSource}
                samples={stageTester.samples}
                onSampleSelect={stageTester.setSampleId}
                customJson={stageTester.state.customJson ?? ''}
                onCustomChange={stageTester.setCustomJson}
                lastOutputRef={stageTester.state.lastOutputRef}
                onLastOutputRefChange={stageTester.setLastOutputRef}
                isLoadingSamples={stageTester.isLoadingSamples}
                samplesError={stageTester.samplesError || undefined}
                customJsonError={stageTester.state.customJsonError || undefined}
                lastOutputError={stageTester.state.lastOutputError || undefined}
                data-testid={`${testId}-inputs`}
              />
            </div>

            {/* Prompt Preview Section */}
            <div>
              <h4
                className="text-sm font-mono font-medium mb-3"
                style={{
                  color: 'var(--accent)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Prompt Preview
              </h4>
              <PromptPreview
                stageId={stageId}
                effectiveInput={stageTester.effectiveInput}
                promptPreview={state.promptPreview}
                promptHash={state.promptHash}
                validationRequestId={state.validationRequestId}
                onValidate={(requestId) => onChange({ validationRequestId: requestId })}
                onLog={onLog}
                data-testid={`${testId}-prompt`}
              />
            </div>

            {/* Run Controls Section */}
            <div>
              <h4
                className="text-sm font-mono font-medium mb-3"
                style={{
                  color: 'var(--accent)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Run Controls
              </h4>
              <RunControls
                stageId={stageId}
                overrides={runControlsOverrides}
                sandboxMode={state.sandboxMode}
                saveToExecution={state.saveToExecution}
                isRunning={state.isRunning}
                lastRun={state.lastRun}
                onOverridesChange={handleOverridesChange}
                onModeChange={handleModeChange}
                onRun={onRun}
                onCancel={onCancel}
                onLog={onLog}
                data-testid={`${testId}-controls`}
              />
            </div>

            {/* Results Section */}
            {state.lastRun && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4
                    className="text-sm font-mono font-medium"
                    style={{
                      color: 'var(--accent)',
                      fontFamily: 'var(--font-family-mono)',
                    }}
                  >
                    Results
                  </h4>
                  <button
                    onClick={copyStageStatus}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
                    style={{
                      color: 'var(--fg-muted)',
                      fontFamily: 'var(--font-family-mono)',
                    }}
                    title="Copy stage status as JSON"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>

                <div
                  className="p-3 rounded border font-mono text-sm"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-primary)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span style={{ color: 'var(--fg-muted)' }}>Status:</span>{' '}
                      <span style={{ color: state.lastRun.success ? 'var(--success)' : 'var(--danger)' }}>
                        {state.lastRun.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--fg-muted)' }}>Duration:</span>{' '}
                      {state.lastRun.duration}ms
                    </div>
                    <div>
                      <span style={{ color: 'var(--fg-muted)' }}>Cost:</span>{' '}
                      ${state.lastRun.cost.toFixed(4)}
                    </div>
                    <div>
                      <span style={{ color: 'var(--fg-muted)' }}>Tokens:</span>{' '}
                      {state.lastRun.tokens.input}→{state.lastRun.tokens.output}
                    </div>
                  </div>

                  {state.lastRun.requestId && (
                    <div className="mt-2 pt-2 border-t text-xs" style={{ borderColor: 'var(--border-subtle)' }}>
                      <span style={{ color: 'var(--fg-muted)' }}>Request ID:</span>{' '}
                      <code>{state.lastRun.requestId.slice(-12)}</code>
                    </div>
                  )}
                </div>

                {/* Stage Output/Error Display */}
                {(state.lastRun.output || state.lastRun.error) && (
                  <div className="mt-4">
                    <h5
                      className="text-sm font-mono font-medium mb-2"
                      style={{
                        color: 'var(--accent)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      {state.lastRun.success ? 'Output' : 'Error'}
                    </h5>
                    <div
                      className="p-3 rounded border font-mono text-sm h-48 overflow-y-auto"
                      style={{
                        backgroundColor: state.lastRun.success ? 'var(--bg-app)' : 'var(--danger-bg)',
                        borderColor: state.lastRun.success ? 'var(--border-default)' : 'var(--danger)',
                        color: state.lastRun.success ? 'var(--fg-primary)' : 'var(--danger)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      <pre className="whitespace-pre-wrap">
                        {state.lastRun.success
                          ? (typeof state.lastRun.output === 'string'
                              ? state.lastRun.output
                              : JSON.stringify(state.lastRun.output, null, 2)
                            )
                          : state.lastRun.error
                        }
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Local Logs Section (Optional, collapsed by default) */}
            <div>
              <button
                onClick={() => setShowLocalLogs(!showLocalLogs)}
                className="flex items-center gap-2 text-sm font-mono font-medium mb-3 transition-colors focus:outline-none"
                style={{
                  color: 'var(--fg-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {showLocalLogs ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                Local Logs (Optional)
              </button>

              {showLocalLogs && (
                <div
                  className="p-3 rounded border text-xs font-mono"
                  style={{
                    backgroundColor: 'var(--bg-app)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  Use the main logs panel for stage-specific filtering
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}